import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { FileText, Github } from "lucide-react";
import HashTag from "../components/HashTag";
import {
  Story,
  StoryHero,
  StoryNav,
  StoryOutro,
  Section,
  Figure,
  Stat,
  StatRow,
  Card,
  Eq,
  Prose,
  Split,
  Bullets,
  Steps,
  fade,
} from "../components/story";

const IMG = "/images/projects/leo-pnt";

const ARXIV = "https://arxiv.org/abs/2603.15908";
const GITHUB = "https://github.com/soham12112/differential_doppler_iridium";

const TAGS = ["GNSS", "PNT", "LEO", "Doppler", "SnapshotPositioning", "SDR", "WildlifeTracking"];

const NAV = [
  ["problem", "The problem"],
  ["why-leo", "Why LEO"],
  ["observable", "The observable"],
  ["differential", "Going differential"],
  ["hardware", "Hardware"],
  ["experiment", "Experiment"],
  ["results", "Results"],
  ["limits", "Limits"],
  ["next", "Next steps"],
];

export default function LeoPntStory() {
  return (
    <Story>
      <Helmet>
        <title>LEO Satellites for Positioning (PNT) | Soham Desai</title>
        <meta
          name="description"
          content="GNSS needs seconds of continuous signal. Snapshot differential Doppler positioning using Iridium LEO satellites works from 5–10 second bursts, cutting position error ~47% over a 30 km baseline. IEEE Radar Conference 2026."
        />
        <meta property="og:title" content="LEO Satellites for Positioning (PNT)" />
        <meta
          property="og:description"
          content="Snapshot differential Doppler positioning with LEO satellites: 5–10 second bursts, ~47% error reduction, validated on real SDR hardware in the SF Bay."
        />
        <meta property="og:image" content={`${IMG}/fig-3.png`} />
      </Helmet>

      <StoryHero
        breadcrumb={{ to: "/projects/leo-pnt", label: "LEO PNT" }}
        kicker="Signals of opportunity · IEEE RadarConf 2026"
        title={
          <>
            LEO Satellites for <span className="text-accent">Positioning</span>
          </>
        }
        lead={
          <>
            Conventional GNSS wants several seconds of continuous reception from satellites
            20,000 km up. Plenty of receivers cannot give it that, a tagged animal at the
            surface, a power-starved sensor, anything jammed or shadowed. This is a
            snapshot-based differential positioning framework that works from{" "}
            <strong className="text-foreground">5–10 second bursts</strong> of low-Earth-orbit
            signal, and the field experiment that shows it holds up.
          </>
        }
        stats={[
          { value: "~47", unit: "%", label: "position-error reduction, measured" },
          { value: "497 → 265", unit: "m", label: "standard vs differential solution" },
          { value: "3", label: "satellites common to rover and base in the reported solve", amber: true },
        ]}
        chips={[
          "Iridium LEO downlink",
          "30 km baseline · SF Bay Area",
          "Accepted, IEEE Radar Conference 2026",
        ]}
      />

      <StoryNav items={NAV} />

      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fade}
          className="flex flex-wrap gap-x-5 gap-y-2 pt-8"
        >
          <a
            href={ARXIV}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-accent text-sm hover:text-accent-hover transition-colors"
          >
            <FileText className="w-4 h-4" /> Preprint on arXiv
          </a>
          <a
            href={GITHUB}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-accent text-sm hover:text-accent-hover transition-colors"
          >
            <Github className="w-4 h-4" /> Code on GitHub
          </a>
        </motion.div>

        {/* ══════════ 1 · PROBLEM ══════════ */}
        <Section
          id="problem"
          kicker="Why · the problem"
          title="GNSS assumes you can keep listening"
          lead="A GPS receiver acquires, locks, tracks, and only then produces a fix. That pipeline assumes an uninterrupted view of the sky for several seconds and a power budget that can sustain it. A whole class of receivers can offer neither."
        >
          <Bullets
            items={[
              <>
                <strong className="text-foreground">Intermittent visibility.</strong> A tagged
                marine animal is at the surface for a couple of seconds at a time, then gone for
                an hour.
              </>,
              <>
                <strong className="text-foreground">Power.</strong> A receiver that must sleep
                between bursts cannot afford to hold a tracking loop open.
              </>,
              <>
                <strong className="text-foreground">Vulnerability.</strong> GNSS signals arrive
                from MEO at roughly −130 dBm, and the L-band frequencies are well known, easy to
                jam, and easy to spoof.
              </>,
            ]}
          />

          <Card accent title="The question">
            <p>
              Not "how do we make GNSS acquire faster", but{" "}
              <em className="text-foreground not-italic font-medium">
                what can you compute from a few seconds of whatever is overhead
              </em>
              , with no prior lock, and then go back to sleep?
            </p>
          </Card>
        </Section>

        {/* ══════════ 2 · WHY LEO ══════════ */}
        <Section
          id="why-leo"
          kicker="The premise"
          title="Low orbits change the arithmetic"
          lead="LEO constellations were built for communication, not navigation, which turns out to be exactly why they help. Iridium orbits about 26× lower than GPS, so the signal arrives far stronger, and the satellites sweep across the sky fast enough that even a short observation carries real geometric information."
        >
          <StatRow cols={3}>
            <Stat value="~780" unit="km" label="Iridium orbital altitude" />
            <Stat value="~20,200" unit="km" label="GPS orbital altitude" amber />
            <Stat value="~28" unit="dB" label="free-space path loss saved by the lower orbit alone" />
          </StatRow>

          <Prose>
            <p>
              That 28 dB is just the range term, 20·log₁₀(20200/780); actual link margin also
              depends on transmit power, frequency and antenna gain, which differ between the two
              systems. But the direction is unambiguous, and it matters twice over.
            </p>
            <p>
              First, a stronger signal means a shorter integration time, which is the whole game
              for a snapshot receiver. Second, a fast-moving satellite produces a{" "}
              <strong className="text-foreground">large, rapidly-changing Doppler shift</strong>,
              so a few seconds of frequency history already traces a distinctive curve, where the
              same window on a slow MEO satellite would be nearly a straight line carrying almost
              no information.
            </p>
            <p>
              LEO constellations also occupy a range of spectrum, MSS bands plus terrestrial L and
              S, so a receiver is not confined to the one band everybody knows how to jam.
            </p>
          </Prose>

          <Figure
            src={`${IMG}/fig-1.png`}
            alt="Geometry: three LEO satellites observed simultaneously by a shore monitoring base station and a rover receiver on a whale at the sea surface"
            caption="The geometry. A monitoring base station at a surveyed location, disciplined by a GPS-disciplined oscillator, and a rover receiver (SDR plus IMU) at an unknown position, both observing the same LEO satellites. In the motivating application the rover is on a surfacing whale."
          />
        </Section>

        {/* ══════════ 3 · OBSERVABLE ══════════ */}
        <Section
          id="observable"
          kicker="The method · part 1"
          title="Position from the shape of the Doppler curve"
          lead="With no code lock and no pseudorange, the only thing a snapshot receiver reliably measures is frequency. Fortunately, frequency is enough."
        >
          <Prose>
            <p>
              As a satellite passes, the received carrier is shifted by the projection of its
              velocity onto the line of sight: positive while approaching, zero at closest
              approach, negative while receding. The{" "}
              <strong className="text-foreground">timing, slope and curvature</strong> of that
              zero-crossing depend on where the receiver is standing.
            </p>
          </Prose>

          <Eq>
            f<sub>D</sub> = (1/c)·f<sub>B</sub>·ρ̇ + f<sub>O</sub> + ḟ<sub>O</sub>·t
          </Eq>

          <Prose>
            <p>
              The solver treats the unknown as a state vector{" "}
              <span className="font-mono text-foreground">
                s = (φ, λ, h, f<sub>O</sub>, ḟ<sub>O</sub>)
              </span>
              , receiver latitude, longitude, height, plus the receiver's own oscillator offset and
              drift. Trial states generate trial Doppler curves; the curve is fitted iteratively
              against the measurement until it matches. The initial guess comes for free from the
              zero-Doppler instant, which alone puts the receiver somewhere on the satellite's
              closest-approach locus.
            </p>
            <p className="text-sm text-muted-foreground">
              Note what is <em>not</em> assumed: no code phase, no navigation-message decode, no
              continuous tracking. Just frequency, over a short arc.
            </p>
          </Prose>
        </Section>

        {/* ══════════ 4 · DIFFERENTIAL ══════════ */}
        <Section
          id="differential"
          kicker="The method · part 2"
          title="Cancelling the errors you cannot measure"
          lead="Doppler positioning from LEO has one dominant error source, and it is not the receiver. Iridium orbits are propagated from public two-line elements, whose position error is typically at the kilometre level and grows with the age of the element set, and the satellite's own clock drifts on top of that. Both errors are effectively identical for two receivers a short distance apart, which is precisely what makes them removable."
        >
          <Figure
            src={`${IMG}/fig-2.png`}
            alt="Algorithm flow: base station computes Doppler residuals which correct the rover measurement before the iterative position solve"
            caption="The differential pipeline. The base station knows where it is, so it can compute the theoretical Doppler for each common satellite and subtract it from what it measured, producing a residual error e(t) that is almost entirely TLE and satellite-clock error. That residual, interpolated in time, is applied to the rover's measurement before the iterative solve runs."
          />

          <Steps
            items={[
              [
                "1",
                "Measure at both ends",
                "Base and rover record Doppler for the same satellite pass, over the same short window.",
              ],
              [
                "2",
                "Compute the residual",
                "The base station's known position gives a theoretical Doppler curve; the difference from its measurement is the common-mode error e(t).",
              ],
              [
                "3",
                "Correct the rover",
                "f_corr = f_meas − e(t), interpolated onto the rover's timestamps. TLE and satellite-clock error largely cancel.",
              ],
              [
                "4",
                "Solve",
                "Seed from the zero-Doppler point, then iterate the state vector until the trial curve fits the corrected measurement.",
              ],
            ]}
          />

          <Card title="Why the cancellation works">
            <p>
              A TLE error displaces the satellite by the same amount as seen from both stations,
              and a satellite clock offset is by definition identical for everyone listening. Over
              a baseline that is small compared with the 780 km orbital altitude, the two receivers
              are looking at nearly the same geometry, so the errors subtract out and the
              genuinely different part, the rover's own position, survives.
            </p>
          </Card>
        </Section>

        {/* ══════════ 5 · HARDWARE ══════════ */}
        <Section
          id="hardware"
          kicker="The build"
          title="Two receivers, both cheap"
          lead="Nothing here is specialised navigation hardware. Both ends are commodity software-defined radios with wideband antennas, which is deliberate: the argument is only interesting if the receiver is something you could actually put on a tag or a buoy."
        >
          <Split>
            <Figure
              src={`${IMG}/fig-3.png`}
              alt="Monitoring base station and rover station hardware: SDR boards, LNAs and Taoglas MAXIMUS wideband antennas"
              caption="Top, the monitoring base station: an SDR board with a low-noise amplifier and a Taoglas MAXIMUS FXUB66 ultra-wideband antenna, disciplined by a GPSDO so its time and frequency reference is trustworthy. Bottom, the rover: a MicroPhase ANTSDR E316 with the same LNA and antenna, hosted by a Raspberry Pi, no external timing reference at all."
            />
            <div className="space-y-5">
              <Prose>
                <p className="font-display text-xl font-semibold text-foreground">
                  Asymmetric by design
                </p>
                <p>
                  The base station gets the good clock, because there is exactly one of it and it
                  sits on shore. The rover is the thing that has to be cheap, small and
                  power-frugal, so it carries no disciplined oscillator; its offset and drift are
                  simply solved for as part of the state vector.
                </p>
              </Prose>
              <Bullets
                items={[
                  "Base: SDR + LNA + wideband antenna, GPSDO-disciplined, surveyed location.",
                  "Rover: ANTSDR E316 + Raspberry Pi + LNA + the same antenna, free-running clock.",
                  "Capture: 5–10 s of raw IQ per burst, then the receiver can drop to a low-power state.",
                  "An IMU rides along on the rover, the hook into the dead-reckoning work.",
                ]}
              />
            </div>
          </Split>
        </Section>

        {/* ══════════ 6 · EXPERIMENT ══════════ */}
        <Section
          id="experiment"
          kicker="The experiment"
          title="A 30 km baseline across the Bay"
          lead="The two stations were separated by roughly 30 km in the San Francisco Bay Area, recording Iridium downlink bursts simultaneously. The rover's true position was surveyed independently, so every solution could be scored against truth rather than against another estimate."
        >
          <Card>
            <Bullets
              items={[
                "Snapshot windows of 5–10 seconds, matching what an intermittent receiver could realistically capture.",
                "Position solved twice per burst: once from the rover alone (standard), once with the base station's differential correction applied.",
                "The hardest case retained rather than discarded: bursts where only three satellites were simultaneously visible to both stations.",
              ]}
            />
          </Card>

          <Prose>
            <p>
              Three common satellites is a deliberately unfavourable configuration. Keeping those
              solves in the result set is the difference between reporting what the method does and
              reporting its best day.
            </p>
          </Prose>
        </Section>

        {/* ══════════ 7 · RESULTS ══════════ */}
        <Section
          id="results"
          kicker="Results"
          title="497 m becomes 265 m"
          lead="The differential correction removed roughly 233 m of error, a 46.8% reduction, on a snapshot solve with only three common satellites."
        >
          <Figure
            src={`${IMG}/fig-4.png`}
            alt="Spatial error map comparing the standard and differentially-corrected rover position estimates against truth"
            caption="Spatial error map. The green star is the surveyed base station and the dark blue diamond is the rover's true location. The red cross is the rover position estimated without differential correction, 497.3 m out; the light blue circle is the same burst with correction applied, 264.5 m out. The correction moves the estimate along the error direction, which is what you expect if the residual really was common-mode."
          />

          <StatRow cols={3}>
            <Stat value="497.3" unit="m" label="standard snapshot Doppler solution" amber />
            <Stat value="264.5" unit="m" label="with differential correction" />
            <Stat value="233" unit="m" label="error removed (46.8%)" />
          </StatRow>

          <Prose>
            <p>
              Two hundred and sixty metres is not a GPS fix, and it is not presented as one. The
              claim is narrower and more useful: from{" "}
              <strong className="text-foreground">
                a few seconds of a communication signal that was never designed for navigation
              </strong>
              , with a free-running clock and three satellites, you can get a hundreds-of-metres
              position where a conventional receiver returns nothing at all. For an animal that
              surfaces for two seconds, that is the difference between a track and no track.
            </p>
            <p>
              The work was accepted at the{" "}
              <strong className="text-foreground">IEEE Radar Conference 2026</strong>; the preprint
              is{" "}
              <a
                href={ARXIV}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent-hover underline underline-offset-4"
              >
                on arXiv
              </a>{" "}
              and the solver is{" "}
              <a
                href={GITHUB}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:text-accent-hover underline underline-offset-4"
              >
                open source
              </a>
              .
            </p>
          </Prose>
        </Section>

        {/* ══════════ 8 · LIMITS ══════════ */}
        <Section
          id="limits"
          kicker="Honesty · what this does not claim"
          title="What still limits the fix"
          lead="The residual error after correction is not noise, it is structure that the differential model does not reach."
        >
          <Card>
            <Bullets
              items={[
                "Hundreds of metres, not metres. This is a coarse-position technique; it complements a precise snapshot method rather than replacing one.",
                "Three common satellites is weak geometry. More satellites in view would tighten the solve considerably, and the constellation, not the algorithm, sets that.",
                "Differential cancellation degrades as the baseline grows, because the two stations stop seeing the same geometry.",
                "Iridium bursts are not a navigation signal. Timing, duration and structure are whatever the communication system happens to emit.",
                "A single deployment and a single baseline. Broad accuracy claims need many more passes across varied geometry.",
              ]}
            />
          </Card>
        </Section>

        {/* ══════════ 9 · NEXT ══════════ */}
        <Section
          id="next"
          kicker="Next steps"
          title="Where this goes next"
          lead="The natural direction is more signals, tighter observables, and fusing this with the methods that are strong exactly where it is weak."
        >
          <motion.div variants={fade} className="grid gap-4 sm:grid-cols-2">
            {[
              [
                "Multi-constellation signals of opportunity",
                "Iridium is one of several LEO constellations transmitting continuously. Combining sources multiplies the usable geometry within the same short snapshot window.",
              ],
              [
                "Carrier phase, not just Doppler",
                "Doppler is the time derivative of carrier phase, so it discards information the phase itself carries. Working the phase directly opens a far tighter observable, once cycle ambiguities can be handled within a burst.",
              ],
              [
                "Fusion with snapshot GNSS",
                "LEO Doppler and coarse-time GNSS snapshots fail in different conditions. Inverse-variance fusion of the two is what the whale-tag work builds on.",
              ],
              [
                "Onboard, real-time solving",
                "Today the solve is offline. Running it on the rover would let a tag report position instead of raw IQ, which is the difference between a research rig and a product.",
              ],
            ].map(([title, body]) => (
              <div
                key={title}
                className="rounded-lg border border-border bg-surface/50 p-5 hover:border-accent/30 transition-colors"
              >
                <h3 className="font-display text-base font-semibold text-foreground mb-2">
                  {title}
                </h3>
                <p className="text-sm text-muted leading-relaxed">{body}</p>
              </div>
            ))}
          </motion.div>
        </Section>

        <StoryOutro
          title="Navigation from signals that were never meant for it"
          tags={TAGS}
          TagComponent={HashTag}
          backTo="/projects/leo-pnt"
          backLabel="More from LEO PNT"
        >
          <p className="mt-4 text-lg">
            The receiver never locks onto anything. It wakes up, records a few seconds of a
            communication satellite it does not control, and goes back to sleep, and that is
            enough to place it, because a differential reference removes the errors it could never
            have estimated on its own.
          </p>
          <p>
            It is a coarse answer to a problem that otherwise has no answer at all, and it is the
            direct ancestor of the snapshot-plus-fusion work on whale tags.
          </p>
        </StoryOutro>
      </div>
    </Story>
  );
}
